import getDecryptedToken from "../../../../api/getDecryptedToken";

export function isRoomAllocationEnabled(): boolean {
  return getDecryptedToken("featureRoomAllocation") === "true";
}

export function getLocationColumnLabel(tokenOnly = false): string {
  if (tokenOnly) return "Token";
  return isRoomAllocationEnabled()
    ? "Session / Room / Token"
    : "Session / Room";
}

export const APPOINTMENT_TABLE_HEADERS = [
  { key: "datetime", label: "Date & Time", align: "left" as const },
  { key: "token", label: "Token", align: "center" as const },
  { key: "regno", label: "Reg No", align: "left" as const },
  { key: "patient", label: "Patient", align: "left" as const },
  { key: "phone", label: "Phone", align: "left" as const },
  { key: "purpose", label: "Purpose", align: "left" as const },
  { key: "doctor", label: "Doctor", align: "left" as const },
  { key: "location", label: "Session / Room / Token", align: "left" as const },
  { key: "status", label: "Status", align: "left" as const },
  { key: "action", label: "Action", align: "center" as const },
];

/** Column order for dentist My Appointments table */
export const MY_APPOINTMENT_TABLE_HEADERS = [
  { key: "datetime", label: "Date & Time", align: "left" as const },
  { key: "token", label: "Token", align: "center" as const },
  { key: "regno", label: "Reg No", align: "left" as const },
  { key: "patient", label: "Patient", align: "left" as const },
  { key: "phone", label: "Phone", align: "left" as const },
  { key: "purpose", label: "Purpose", align: "left" as const },
  { key: "patientType", label: "Status", align: "left" as const },
  { key: "status", label: "Action", align: "left" as const },
  { key: "action", label: "Profile", align: "center" as const },
];

export function patientTypeLabelFromItem(item: {
  patientTypeLabel?: string | null;
  registerdDate?: string | Date | null;
  createdAt?: string | Date | null;
  patientId?: {
    registerdDate?: string | Date | null;
    createdAt?: string | Date | null;
  };
}): string {
  if (item?.patientTypeLabel) return item.patientTypeLabel;

  const created =
    item?.registerdDate ||
    item?.createdAt ||
    item?.patientId?.registerdDate ||
    item?.patientId?.createdAt;
  if (created) {
    const d = created instanceof Date ? created : new Date(created);
    if (!Number.isNaN(d.getTime())) {
      const today = new Date();
      const sameDay =
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate();
      if (sameDay) return "New Patient";
    }
  }
  return "Existing Patient";
}

/** Long-term estimate remaining due (same formula as invoice / case treatment). */
export function longTermDueFromTreatments(treatments: any[]): number {
  let due = 0;
  for (const item of treatments || []) {
    if (item?.status !== "long term") continue;
    const estimate = parseFloat(item?.price) || 0;
    const billed = (item?.subTreatment || []).reduce((sum: number, sub: any) => {
      const isPriced =
        sub?.selected_option === "treatment_paid" ||
        sub?.selected_option === "treatment_stage";
      if (
        !isPriced ||
        sub?.status === "long term" ||
        sub?.status === "planned"
      ) {
        return sum;
      }
      const price = parseFloat(sub?.price);
      return sum + (Number.isFinite(price) ? price : 0);
    }, 0);
    due += Math.max(0, estimate - billed);
  }
  return due;
}

export function resolvePatientTypeLabelFromPatientAndTreatments(
  patient: any,
  treatmentDocs: any[] = []
): string {
  if (patient?.patientTypeLabel === "Treatment Plan") return "Treatment Plan";

  const allItems = (treatmentDocs || []).flatMap(
    (doc) => doc?.treatments || doc?.treatment || []
  );
  if (longTermDueFromTreatments(allItems) > 0) return "Treatment Plan";

  return patientTypeLabelFromItem(patient || {});
}

export const PATIENT_TYPE_STYLES: Record<string, string> = {
  "Treatment Plan":
    "bg-amber-200 dark:bg-amber-500/30 text-amber-900 dark:text-amber-100 border-amber-400 dark:border-amber-500",
  "New Patient":
    "bg-emerald-200 dark:bg-emerald-500/30 text-emerald-900 dark:text-emerald-100 border-emerald-400 dark:border-emerald-500",
  "Existing Patient":
    "bg-indigo-200 dark:bg-indigo-500/30 text-indigo-900 dark:text-indigo-100 border-indigo-400 dark:border-indigo-500",
};

/** Short UI labels for patient visit type badges */
export const PATIENT_TYPE_SHORT_LABELS: Record<string, string> = {
  "Treatment Plan": "Plan",
  "New Patient": "New",
  "Existing Patient": "Exist",
};

export function formatPatientTypeLabel(label?: string | null): string {
  if (!label) return PATIENT_TYPE_SHORT_LABELS["Existing Patient"];
  return PATIENT_TYPE_SHORT_LABELS[label] || label;
}

export function patientMobileFromItem(item: {
  patientId?: { mobileNo?: string | null };
  bookPatientId?: { mobileNo?: string | null };
  mobileNo?: string | null;
}): string {
  return (
    item?.patientId?.mobileNo ||
    item?.bookPatientId?.mobileNo ||
    item?.mobileNo ||
    "—"
  );
}

export function patientRegNoFromItem(item: {
  patientId?: { regNo?: string | number | null };
  bookPatientId?: { regNo?: string | number | null };
  regNo?: string | number | null;
}): string {
  const raw =
    item?.patientId?.regNo ?? item?.bookPatientId?.regNo ?? item?.regNo;
  const value = String(raw ?? "").trim();
  return value || "—";
}

/** Lifecycle status labels (API + legacy aliases for display). */
export const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
  scheduled: "Scheduled",
  upcoming: "Upcoming",
  appointment: "Upcoming",
  in_queue: "In Queue",
  inqueue: "In Queue",
  waiting: "In Queue",
  ongoing: "Ongoing",
  complete: "Complete",
  completed: "Complete",
  cancelled: "Cancelled",
  canceled: "Cancelled",
  no_show: "No-Show",
  noshow: "No-Show",
  rescheduled: "Rescheduled",
  tray: "Reschedule Tray",
  // Legacy booking-origin statuses (pre-migration display)
  walkin: "Walk-in",
  callin: "Call-in",
};

/** Lifecycle-only options for status selects (no walk-in / call-in). */
export const APPOINTMENT_STATUS_OPTIONS = [
  { value: "upcoming", label: "Upcoming" },
  { value: "in_queue", label: "In Queue" },
  { value: "ongoing", label: "Ongoing" },
  { value: "complete", label: "Complete" },
  { value: "cancelled", label: "Cancelled" },
  { value: "no_show", label: "No-Show" },
] as const;

export const BOOKING_SOURCE_LABELS: Record<string, string> = {
  pre_booked: "Schedule",
  walk_in: "Walk-in",
  call_in: "Call-in",
  online: "Online",
  // Legacy aliases
  schedule: "Schedule",
  scheduled: "Schedule",
  walkin: "Walk-in",
  callin: "Call-in",
};

export const statusStyles: Record<string, string> = {
  scheduled:
    "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-600",
  upcoming:
    "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-600",
  appointment:
    "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-600",
  in_queue:
    "bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-600",
  inqueue:
    "bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-600",
  waiting:
    "bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-600",
  ongoing:
    "bg-lime-100 dark:bg-lime-900/40 text-lime-700 dark:text-lime-300 border-lime-300 dark:border-lime-600",
  complete:
    "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-600",
  completed:
    "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-600",
  cancelled:
    "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-300 dark:border-red-600",
  canceled:
    "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-300 dark:border-red-600",
  no_show:
    "bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-500",
  noshow:
    "bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-500",
  walkin:
    "bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 border-teal-300 dark:border-teal-600",
  callin:
    "bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-600",
  reschudle:
    "bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-600",
  rescheduled:
    "bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-600",
  default:
    "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600",
};

export const bookingSourceStyles: Record<string, string> = {
  pre_booked:
    "bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 border-orange-300 dark:border-orange-600",
  walk_in:
    "bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200 border-teal-300 dark:border-teal-600",
  call_in:
    "bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200 border-cyan-300 dark:border-cyan-600",
  online:
    "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200 border-indigo-300 dark:border-indigo-600",
  default:
    "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-500",
};

/** Normalize booking_source (and legacy status/type aliases) to API keys. */
export function resolveBookingSource(raw?: string | null): string {
  const key = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  if (
    key === "pre_booked" ||
    key === "prebooked" ||
    key === "schedule" ||
    key === "scheduled"
  ) {
    return "pre_booked";
  }
  if (key === "walk_in" || key === "walkin") return "walk_in";
  if (key === "call_in" || key === "callin") return "call_in";
  if (key === "online") return "online";
  return "";
}

/** UI label for booking_source; pre_booked → Schedule. */
export function formatBookingSource(raw?: string | null): string {
  const normalized = resolveBookingSource(raw);
  if (!normalized) return "";
  return BOOKING_SOURCE_LABELS[normalized] ?? normalized;
}

/**
 * Resolve booking_source from appointment fields, with legacy status/type fallback.
 */
export function bookingSourceFromItem(item?: {
  booking_source?: string | null;
  bookingSource?: string | null;
  status?: string | null;
  type?: string | null;
} | null): string {
  if (!item) return "";
  return (
    resolveBookingSource(item.booking_source) ||
    resolveBookingSource(item.bookingSource) ||
    resolveBookingSource(item.status) ||
    (String(item.type ?? "").trim().toLowerCase() === "online" ? "online" : "")
  );
}

/** Map legacy lifecycle status strings to current API select values. */
export function normalizeLifecycleStatus(raw?: string | null): string {
  const key = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  const map: Record<string, string> = {
    scheduled: "upcoming",
    upcoming: "upcoming",
    appointment: "upcoming",
    in_queue: "in_queue",
    inqueue: "in_queue",
    waiting: "in_queue",
    ongoing: "ongoing",
    complete: "complete",
    completed: "complete",
    cancelled: "cancelled",
    canceled: "cancelled",
    no_show: "no_show",
    noshow: "no_show",
    // Former booking-origin statuses → upcoming for the select
    walkin: "upcoming",
    walk_in: "upcoming",
    callin: "upcoming",
    call_in: "upcoming",
  };
  return map[key] || "";
}

/** Resolve API `status` header from view / status card / source chip. */
export function resolveListStatusHeader(opts: {
  view?: string | null;
  statusCard?: string | null;
  sourceChip?: string | null;
}): string {
  const source = String(opts.sourceChip ?? "").trim();
  if (source) return source;
  const status = String(opts.statusCard ?? "").trim();
  if (status) return status;
  const view = String(opts.view ?? "").trim() || "active";
  if (view === "all") return "pending";
  if (view === "completed") return "complete";
  return view;
}

export const FIELD_LABELS = {
  dateTime: "Date & Time",
  location: "Session / Room / Token",
  doctor: "Doctor",
  status: "Status",
  purpose: "Purpose",
  type: "Type",
  queue: "Token number",
  regNo: "Reg No",
  phone: "Phone",
} as const;

export function formatAppointmentStatus(status?: string | null): string {
  const key = String(status ?? "").trim();
  if (!key) return "—";
  return APPOINTMENT_STATUS_LABELS[key] ?? key;
}

export function getLocationFieldLabel(tokenOnly = false): string {
  return getLocationColumnLabel(tokenOnly);
}

/** Parse queue input; empty string → 0. Returns null if invalid. */
export function parseQueueInput(raw: string): number | null {
  const trimmed = String(raw ?? "").trim();
  if (trimmed === "") return 0;
  if (!/^\d+$/.test(trimmed)) return null;
  const n = parseInt(trimmed, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function formatQueueFromItem(item: { queue?: unknown }): string {
  if (item?.queue != null && item?.queue !== "") {
    return String(Number(item.queue));
  }
  return "";
}

function employeeDisplayName(
  employee?: { firstName?: string; lastName?: string } | null
): string {
  if (!employee) return "";
  return [employee.firstName, employee.lastName].filter(Boolean).join(" ").trim();
}

export function formatAppointmentPurpose(item?: {
  purposeName?: string | string[];
  purposeIds?: Array<{ purposeName?: string } | string>;
  purposeId?: { purposeName?: string | string[] };
  bookTreatmentId?: { name?: string };
} | null): string {
  const fromIds = (item?.purposeIds ?? [])
    .map((p) => (typeof p === "object" && p != null ? p.purposeName : null))
    .filter((name): name is string => Boolean(name && String(name).trim()));
  if (fromIds.length) return fromIds.join(", ");

  const topLevel = item?.purposeName;
  if (Array.isArray(topLevel)) {
    const names = topLevel.map((n) => String(n).trim()).filter(Boolean);
    if (names.length) return names.join(", ");
  } else if (topLevel != null && String(topLevel).trim()) {
    return String(topLevel).trim();
  }

  const single = item?.purposeId?.purposeName;
  if (Array.isArray(single)) {
    const names = single.map((n) => String(n).trim()).filter(Boolean);
    if (names.length) return names.join(", ");
  } else if (single != null && String(single).trim()) {
    return String(single).trim();
  }
  if (item?.bookTreatmentId?.name) return String(item.bookTreatmentId.name);
  return "N/A";
}

/** Mr / Mrs / Dr + full name (used on Visit and patient headers). */
export function formatPatientNameWithTitle(patient?: {
  title?: string | null;
  fullName?: string | null;
} | null): string {
  const name = String(patient?.fullName ?? "").trim();
  let title = String(patient?.title ?? "").trim();
  if (title.endsWith(".")) title = title.slice(0, -1);
  if (title && name) return `${title}. ${name}`;
  return name || title;
}

/** First letter of the given name, ignoring Mr/Mrs/Dr on a display string. */
export function patientAvatarInitial(displayName?: string | null): string {
  const stripped = String(displayName ?? "")
    .trim()
    .replace(/^(mr|mrs|miss|ms|dr|rev|master|baby|prof)\.?\s+/i, "")
    .trim();
  const source = stripped || String(displayName ?? "").trim();
  return (source.charAt(0) || "?").toUpperCase();
}

/** First purpose color on the appointment (purposeIds, then purposeId). */
export function purposeColorFromItem(item?: {
  purposeColor?: string;
  purposeIds?: Array<{ color?: string } | string>;
  purposeId?: { color?: string };
} | null): string | null {
  const top = item?.purposeColor;
  if (top != null && String(top).trim()) return String(top).trim();
  const fromIds = (item?.purposeIds ?? [])
    .map((p) => (typeof p === "object" && p != null ? p.color : null))
    .find((c) => c != null && String(c).trim());
  if (fromIds) return String(fromIds).trim();
  const single = item?.purposeId?.color;
  if (single != null && String(single).trim()) return String(single).trim();
  return null;
}

/** Soft background tint from a purpose color (matches Manage Purpose swatches). */
export function purposeColorBackground(
  color: string,
  opacityPercent = 30
): string {
  return `color-mix(in srgb, ${color} ${opacityPercent}%, transparent)`;
}

function parseCssColor(
  color: string
): { r: number; g: number; b: number; a: number } | null {
  const c = color.trim();
  const hex = c.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1];
    if (h.length === 3) {
      return {
        r: parseInt(h[0] + h[0], 16),
        g: parseInt(h[1] + h[1], 16),
        b: parseInt(h[2] + h[2], 16),
        a: 1,
      };
    }
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
      a: 1,
    };
  }
  const rgba = c.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i
  );
  if (!rgba) return null;
  return {
    r: Number(rgba[1]),
    g: Number(rgba[2]),
    b: Number(rgba[3]),
    a: rgba[4] != null ? Number(rgba[4]) : 1,
  };
}

/** True when a CSS color is already a pale fill (do not mix it down further). */
export function isLightCssColor(color: string): boolean {
  const rgb = parseCssColor(color);
  if (!rgb || Number.isNaN(rgb.r + rgb.g + rgb.b + rgb.a)) return false;
  const r = rgb.r * rgb.a + 255 * (1 - rgb.a);
  const g = rgb.g * rgb.a + 255 * (1 - rgb.a);
  const b = rgb.b * rgb.a + 255 * (1 - rgb.a);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 >= 0.72;
}

/** Slot fill: use light pastels as-is; tint stronger accents so they stay pale. */
export function calendarSlotFill(color: string, mixPercent = 22): string {
  if (isLightCssColor(color)) return color;
  return purposeColorBackground(color, mixPercent);
}

export function purposeIdsFromAppointment(item?: {
  purposeIds?: Array<{ _id?: string; id?: string } | string>;
  purposeId?: { _id?: string; id?: string } | string;
} | null): string[] {
  const fromIds = (item?.purposeIds ?? [])
    .map((p) => (typeof p === "object" && p != null ? p._id ?? p.id : p))
    .filter((id) => id != null && String(id).trim())
    .map((id) => String(id));
  if (fromIds.length) return fromIds;
  const single = item?.purposeId;
  if (single == null) return [];
  if (typeof single === "object") {
    const id = single._id ?? single.id;
    return id != null ? [String(id)] : [];
  }
  return [String(single)];
}

export function formatPurposeIdsFromCatalog(
  purposeIds: string[],
  purposesCatalog?: Array<{ _id?: string; id?: string; purposeName?: string }>
): string {
  const names = purposeIds
    .map((id) => {
      const match = (purposesCatalog ?? []).find(
        (item) => String(item._id ?? item.id) === String(id)
      );
      return match?.purposeName ? String(match.purposeName) : null;
    })
    .filter((name): name is string => Boolean(name));
  if (names.length) return names.join(", ");
  return purposeIds.length ? "—" : "Not selected";
}

export function doctorNameFromItem(item: {
  doctorId?: { firstName?: string; lastName?: string };
}): string {
  return employeeDisplayName(item?.doctorId) || "—";
}

/** Names from tranferdoctor history (DB field name kept as-is). */
export function transferDoctorNamesFromItem(item: {
  tranferdoctor?: Array<{ doctorId?: { firstName?: string; lastName?: string } }>;
}): string {
  const list = item?.tranferdoctor;
  if (!Array.isArray(list) || list.length === 0) return "";
  return list
    .map((entry) => employeeDisplayName(entry?.doctorId))
    .filter(Boolean)
    .join(", ");
}

export function latestTransferDoctorNameFromItem(item: {
  tranferdoctor?: Array<{ doctorId?: { firstName?: string; lastName?: string } }>;
}): string {
  const list = item?.tranferdoctor;
  if (!Array.isArray(list) || list.length === 0) return "";
  return employeeDisplayName(list[list.length - 1]?.doctorId);
}
